import { useCallback, useEffect, useState } from 'react';
import { asTuple } from './asTuple';

type Unsubscribe = () => void;
type Callback<T> = (newValue: T) => void;

export interface PersistentStorage<T> {
  get: () => T,
  set(newValue: T): void,
  listen: (callback: Callback<T>) => Unsubscribe,
}

export const createPersistentStorage = <T>(
  storage: Storage,
  initialValue: T,
  key: string,
  cacheBuster?: string,
): PersistentStorage<T> => {
  if (cacheBuster) {
    const cacheBusterKey = `${key}.CACHE_BUSTER`;
    const previousCacheBuster = storage.getItem(cacheBusterKey);
    if (previousCacheBuster !== cacheBuster) {
      storage.removeItem(key);
    }
    storage.setItem(cacheBusterKey, cacheBuster);
  }

  const callbacks: Callback<T>[] = [];

  return {
    get: () => JSON.parse(storage.getItem(key)!) || initialValue,
    set: newValue => {
      storage.setItem(key, JSON.stringify(newValue));
      callbacks.forEach(c => c(newValue));
    },
    listen: c => {
      callbacks.push(c);
      return () => {
        const i = callbacks.indexOf(c);
        if (i < 0) return;
        callbacks.splice(i, 1);
      };
    },
  };
};

export const usePersistentStorage = <T>(storage: PersistentStorage<T>) => {
  const [value, setValueInState] = useState(storage.get());

  const setValue = useCallback((newValue: T) => {
    storage.set(newValue);
  }, [storage]);

  useEffect(() => {
    storage.listen(setValueInState);
  }, [storage]);

  return asTuple(value, setValue);
};
